import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../icons/icon.component';

export interface StatItem {
  label: string;
  value: string | number;
  colorClass?: string; // e.g., 'text-green-600', 'text-blue-600'
}

export type StatsCardColor =
  | 'blue' | 'green' | 'teal' | 'purple' | 'orange'
  | 'rose' | 'amber' | 'indigo' | 'cyan' | 'violet' | 'emerald';

const COLOR_MAP: Record<StatsCardColor, { iconBg: string; iconColor: string; valueColor: string }> = {
  blue:    { iconBg: 'bg-[#EAF4F3] border border-[#D3EAE8]',    iconColor: 'text-[#237C83]',    valueColor: 'text-[#182E36]'    },
  green:   { iconBg: 'bg-[#EAF6EE] border border-[#D1EED8]',    iconColor: 'text-[#27864D]',    valueColor: 'text-[#27864D]'   },
  teal:    { iconBg: 'bg-[#EAF4F3] border border-[#D3EAE8]',    iconColor: 'text-[#0F766E]',    valueColor: 'text-[#0F766E]'    },
  purple:  { iconBg: 'bg-purple-50 border border-purple-100',   iconColor: 'text-purple-600',  valueColor: 'text-purple-700'  },
  orange:  { iconBg: 'bg-orange-50 border border-orange-100',   iconColor: 'text-orange-600',  valueColor: 'text-orange-700'  },
  rose:    { iconBg: 'bg-[#FFF1F2] border border-[#FFE0E3]',    iconColor: 'text-[#B93841]',    valueColor: 'text-[#B93841]'    },
  amber:   { iconBg: 'bg-[#FFFBEB] border border-[#FEF3C7]',    iconColor: 'text-[#89621E]',   valueColor: 'text-[#89621E]'   },
  indigo:  { iconBg: 'bg-indigo-50 border border-indigo-100',   iconColor: 'text-indigo-600',  valueColor: 'text-indigo-700'  },
  cyan:    { iconBg: 'bg-cyan-50 border border-cyan-100',       iconColor: 'text-cyan-600',    valueColor: 'text-cyan-700'    },
  violet:  { iconBg: 'bg-violet-50 border border-violet-100',   iconColor: 'text-violet-600',  valueColor: 'text-violet-700'  },
  emerald: { iconBg: 'bg-[#EAF6EE] border border-[#D1EED8]',    iconColor: 'text-[#27864D]',   valueColor: 'text-[#27864D]' },
};

@Component({
  selector: 'app-stats-card',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './stats-card.html',
  styleUrl: './stats-card.css',
})
export class StatsCardComponent {
  @Input() title: string = '';
  @Input() value: string | number = '';
  @Input() icon: string = '';
  @Input() isLoading: boolean = false;
  @Input() items: StatItem[] = [];
  @Input() color: StatsCardColor = 'blue';
  @Input() mode: 'simple' | 'detailed' | 'auto' = 'auto';
  @Input() delta: string = '';
  @Input() deltaPositive: boolean = true;

  get currentMode(): 'simple' | 'detailed' {
    if (this.mode !== 'auto') return this.mode;
    return this.items && this.items.length > 0 ? 'detailed' : 'simple';
  }

  get iconBgClass(): string { return COLOR_MAP[this.color]?.iconBg ?? 'bg-blue-100'; }
  get iconColorClass(): string { return COLOR_MAP[this.color]?.iconColor ?? 'text-blue-600'; }
  get valueColorClass(): string { return COLOR_MAP[this.color]?.valueColor ?? 'text-blue-700'; }

  get tablerIconClass(): string {
    if (!this.icon) return '';
    const iconNameMap: Record<string, string> = {
      calenderplus: 'clock',
      calendarplus: 'clock',
      pending: 'clock',
      cart: 'shopping-cart',
      dollar: 'currency-rupee',
      rupee: 'currency-rupee',
      revenue: 'coins',
      inventory: 'package',
      userstats: 'user-star',
      followup: 'calendar-event',
      active: 'percentage',
      telecaller: 'phone-calling',
      doctor: 'stethoscope',
      hospital: 'building-hospital',
      hospialactive: 'building-hospital',
      phonecheck: 'phone-check',
      bookmark: 'bookmark',
      briefcase: 'briefcase',
      dashboard: 'layout-dashboard',
      labs: 'flask',
      refreshcw: 'refresh',
      csv: 'file-spreadsheet',
      purchase: 'receipt',
    };
    const lower = this.icon.toLowerCase().replace(/[-_]/g, '');
    if (iconNameMap[lower]) {
      const mapped = iconNameMap[lower];
      return mapped.startsWith('ti ') ? mapped : 'ti ti-' + mapped;
    }
    // Auto-convert camelCase / PascalCase to kebab-case e.g. userExclamation -> user-exclamation
    const kebab = this.icon.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    if (kebab.startsWith('ti ti-')) return kebab;
    if (kebab.startsWith('ti-')) return 'ti ' + kebab;
    if (kebab.startsWith('ti ')) return kebab;
    return 'ti ti-' + kebab;
  }

  get displayValue(): string {
    const raw = String(this.value);
    // Already pre-formatted (contains letters like K/L/Cr/%) — pass through
    if (/[a-zA-Z%]/.test(raw)) return raw;
    // Parse numeric — strip currency symbol and commas
    const n = parseFloat(raw.replace(/[₹,]/g, ''));
    if (isNaN(n) || n < 10000) return raw;
    const curr = raw.startsWith('₹') ? '₹' : '';
    if (n >= 10000000) return `~${curr}${+(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
    if (n >= 100000)   return `~${curr}${+(n / 100000).toFixed(2).replace(/\.?0+$/, '')}L`;
    return `~${curr}${+(n / 1000).toFixed(1).replace(/\.?0+$/, '')}K`;
  }

  get fullValue(): string {
    return String(this.value);
  }

  showTooltipAtBottom = false;

  checkTooltipPosition(event: Event) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    // If the top of the card is less than 100px from the top of the viewport,
    // show the tooltip at the bottom so it doesn't get cut off by browser header.
    this.showTooltipAtBottom = rect.top < 100;
  }
}
